"use client";

import { useMemo, useRef, useState } from "react";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ArrowRight, ArrowUpRight, TrendingDown } from "@/components/icons";
import { cn } from "@/lib/utils";
import { SectionHeader } from "@/components/ui";
import {
  PricingSlider,
  sliderThumbStyles,
} from "@/components/sections/products/shared/pricing-slider";
import { buildGbTiers } from "@/lib/pricing-utils";
import { useProductsInfo } from "@/lib/products-info";
import { REGISTER_URL, ROUTES } from "@/lib/constants";
import {
  DECODO_MOBILE_PRICING_URL,
  DECODO_RESIDENTIAL_PRICING_URL,
  PRICING_VERIFIED_DATE,
  PROXIDIZE_MOBILE_FALLBACK_TIERS,
  PROXIDIZE_RESIDENTIAL_FALLBACK_TIERS,
  decodoMonthlyCost,
  type ProxyProduct,
} from "./pricing-data";

const MotionDiv = motion.div;

const currency = (n: number) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: n % 1 === 0 ? 0 : 2,
  });

const PRODUCT_META: Record<
  ProxyProduct,
  { label: string; decodoUrl: string; pricingRoute: string }
> = {
  mobile: {
    label: "Mobile proxies",
    decodoUrl: DECODO_MOBILE_PRICING_URL,
    pricingRoute: ROUTES.pricingMobileProxies,
  },
  residential: {
    label: "Residential proxies",
    decodoUrl: DECODO_RESIDENTIAL_PRICING_URL,
    pricingRoute: ROUTES.pricingResidentialProxies,
  },
};

export default function DecodoCostCalculator() {
  const prefersReducedMotion = useReducedMotion();
  const posthog = usePostHog();
  const products = useProductsInfo();
  const hasTracked = useRef(false);

  const [product, setProduct] = useState<ProxyProduct>("mobile");

  // Live Proxidize prices when the pricing API responds; honest static
  // fallbacks (the same ones the pricing pages use) when it doesn't.
  const mobileTiers = useMemo(
    () => buildGbTiers(products?.["Monthly Per GB Mobile"] ?? [], PROXIDIZE_MOBILE_FALLBACK_TIERS),
    [products]
  );
  const residentialTiers = useMemo(
    () =>
      buildGbTiers(
        products?.["Monthly Per GB Residential"] ?? [],
        PROXIDIZE_RESIDENTIAL_FALLBACK_TIERS
      ),
    [products]
  );

  const tiers = product === "mobile" ? mobileTiers : residentialTiers;
  const steps = useMemo(() => tiers.map((t) => t.gb), [tiers]);

  const [gb, setGb] = useState(100);
  const selected =
    tiers.find((t) => t.gb === gb) ??
    tiers.reduce((best, t) => (Math.abs(t.gb - gb) < Math.abs(best.gb - gb) ? t : best), tiers[0]);
  const activeGb = selected.gb;

  const decodo = decodoMonthlyCost(product, activeGb);
  const proxidizeCost = selected.totalPrice;
  const savings = decodo.cost - proxidizeCost;
  const savingsPct = Math.round((savings / decodo.cost) * 100);
  const meta = PRODUCT_META[product];

  function track() {
    if (!hasTracked.current) {
      hasTracked.current = true;
      posthog?.capture("alternatives_calculator_used", { competitor: "decodo" });
    }
  }

  function handleChange(value: number) {
    setGb(value);
    track();
  }

  function handleProductSwitch(next: ProxyProduct) {
    setProduct(next);
    track();
    posthog?.capture("alternatives_calculator_product_switched", {
      competitor: "decodo",
      product: next,
    });
  }

  return (
    <section
      id="cost-calculator"
      className="py-section border-border scroll-mt-24 border-b"
      aria-labelledby="calculator-heading"
    >
      <style>{sliderThumbStyles}</style>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MotionDiv
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 24 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.55 }}
          className="mb-14 max-w-2xl"
        >
          <SectionHeader
            eyebrow="Cost Calculator"
            id="calculator-heading"
            heading="What would your monthly proxy bill be?"
            body="Pick a product, drag the slider to your monthly data usage. Proxidize prices come from our live pricing; Decodo is shown as the smallest published plan that covers your usage."
          />
        </MotionDiv>

        <MotionDiv
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 24 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.55, delay: 0.08 }}
          className="bg-card card-wash card-hover border-border relative overflow-hidden rounded-2xl border p-6 sm:p-8"
        >
          <div className="relative mb-8 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-muted-foreground text-sm">Monthly data usage</p>
              <p className="text-foreground text-3xl font-bold tracking-tight sm:text-4xl">
                {activeGb.toLocaleString("en-US")} GB
              </p>
            </div>

            <div
              role="group"
              aria-label="Proxy product"
              className="border-border inline-flex rounded-lg border p-1"
            >
              {(Object.keys(PRODUCT_META) as ProxyProduct[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={product === key}
                  onClick={() => handleProductSwitch(key)}
                  className={cn(
                    "cursor-pointer rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
                    product === key
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {PRODUCT_META[key].label}
                </button>
              ))}
            </div>
          </div>

          <PricingSlider
            min={steps[0]}
            max={steps[steps.length - 1]}
            value={activeGb}
            onChange={handleChange}
            steps={steps}
            aria-label={`Monthly ${meta.label.toLowerCase()} data usage in gigabytes`}
          />

          <div className="relative mt-10 grid gap-5 sm:grid-cols-2">
            <div
              className="rounded-xl border p-6"
              style={{
                borderColor: "color-mix(in srgb, var(--brand-green) 35%, transparent)",
                backgroundColor: "color-mix(in srgb, var(--brand-green) 5%, transparent)",
              }}
            >
              <p style={{ color: "var(--brand-green)" }} className="text-sm font-semibold">
                Proxidize
              </p>
              <p
                className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl"
                style={{ color: "var(--brand-green)" }}
              >
                {currency(proxidizeCost)}
                <span className="text-muted-foreground text-base font-normal">/mo</span>
              </p>
              <p className="text-muted-foreground mt-1 text-sm">
                {currency(selected.monthlyPPG)}/GB · monthly plan
              </p>
            </div>

            <div className="border-border bg-background/40 rounded-xl border p-6">
              <p className="text-foreground text-sm font-semibold">Decodo</p>
              <p className="text-foreground mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
                {currency(decodo.cost)}
                <span className="text-muted-foreground text-base font-normal">/mo</span>
              </p>
              <p className="text-muted-foreground mt-1 text-sm">{decodo.planLabel}</p>
            </div>
          </div>

          <div aria-live="polite">
            {savings > 0 ? (
              <p className="text-foreground mt-6 flex items-center gap-2 text-sm font-medium">
                <TrendingDown
                  size={16}
                  style={{ color: "var(--brand-green)" }}
                  aria-hidden="true"
                />
                You&rsquo;d save {currency(savings)}/month ({savingsPct}%) with Proxidize at this
                volume.
              </p>
            ) : (
              <p className="text-muted-foreground mt-6 text-sm font-medium">
                At this volume Decodo&rsquo;s published plan is the cheaper option.
              </p>
            )}
          </div>

          <p className="text-muted-foreground mt-4 text-xs leading-relaxed">
            Decodo sells fixed-data plans (top-ups available), so the fair comparison is the plan
            you&rsquo;d actually buy
            {decodo.extrapolated
              ? ", above their largest published plan we estimate at their top per-GB rate"
              : ""}
            . Decodo pricing verified {PRICING_VERIFIED_DATE};{" "}
            <a
              href={meta.decodoUrl}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="hover:text-foreground underline underline-offset-2 transition-colors"
            >
              check their live pricing
            </a>
            .
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <span className="cta-glow-btn-wrap">
              <a
                href={REGISTER_URL}
                onClick={() =>
                  posthog?.capture("alternatives_cta_clicked", {
                    competitor: "decodo",
                    location: "calculator",
                    product,
                    gb: activeGb,
                  })
                }
                className="cta-glow-btn-inner"
              >
                Try Proxidize NOW
                <ArrowRight size={16} className="ml-2" aria-hidden="true" />
              </a>
            </span>
            <Link
              href={meta.pricingRoute}
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm font-medium transition-colors"
            >
              Full Proxidize {product} pricing
              <ArrowUpRight size={14} aria-hidden="true" />
            </Link>
          </div>
        </MotionDiv>

        <MotionDiv
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 24 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.55, delay: 0.12 }}
          className="border-border bg-card mt-5 rounded-xl border p-6"
        >
          <p className="text-foreground text-sm font-semibold">
            Need stable exits of your own instead of a pool?
          </p>
          <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">
            Proxidize also sells per-proxy mobile plans, real mobile IPs assigned to your account
            with unlimited bandwidth, per-proxy rotation control, and city/ISP selection, from{" "}
            <strong className="text-foreground font-semibold">$59/proxy/month</strong>.
            Decodo&rsquo;s public mobile pricing is bandwidth/package based, with no comparable
            per-proxy plan listed.{" "}
            <Link
              href={`${ROUTES.productMobileProxies}#mobile-product-models`}
              className="text-brand hover:text-brand/80 font-semibold transition-colors"
            >
              See per-proxy plans →
            </Link>
          </p>
        </MotionDiv>
      </div>
    </section>
  );
}
